import { BaseModel } from './base-model';
import { NotificationTypes, VoyNotification } from '../services/types/vessel-notifications-services.types';
import { IMO } from './voyage-imo-model';
import { VoyagePlan } from './vessel-planning-model';

export enum LoadstarEvents {
    ExistsUpdated = 'loadstar-exists-updated',
    ReviewChanges = 'loadstar-review-changes',
}

export const LOADSTAR_REVIEWED_ALERT_ID = 'loadstar-reviewed-alert-id';
export type LoadstarNotificationType = VoyNotification<NotificationTypes.Loadstar>;

export interface LoadstarValues {
    fore_draft: number;
    aft_draft: number;
    fluid_gm: number;
    solid_gm: number;
    live_reefers: number;
    max_roll_angle: number;
    schedule_id: string;
    voyage_number: string;
    departure_port: string;
    arrival_port: string;
    saved_at: string;
}

export type EditableLoadstarValues = Pick<
    LoadstarValues,
    'fore_draft' |
    'aft_draft' |
    'fluid_gm' |
    'solid_gm' |
    'live_reefers' |
    'max_roll_angle'
>;

// Tracks which plan fields currently hold a value that originated from LoadStar,
// persisted as `plan.passagePlan.userInput.loadstarSourceMask` so the "LoadStar" tag
// can be restored on reopen without re-deriving it from value comparison (which would
// false-positive if a user happens to type the same number LoadStar reported).
//
// The mask alone is NOT sufficient to decide tag visibility: it's only written when the
// user explicitly saves voy-drafts-gmt-popup / voyage-leg-details, or accepts a LoadStar
// review. Plans saved before this existed - or a brand-new plan before its first save -
// have no mask, even though an unset field (sentinel `-1`/`null`) is still showing the
// LoadStar fallback value. Callers (voy-drafts-gmt-popup.ts, voyage-leg-details.ts) must
// OR the decoded bit with that "field is unset and LoadStar has a value" check:
//
//   _isXFromLoadstar = decodeLoadstarSource(mask).x || (planValueIsSentinel && loadstarValue !== undefined)
//
// Scenarios:
//   | Mask bit | Plan value | LoadStar value | Edited this session | _isXFromLoadstar | Tag shown |
//   |----------|------------|----------------|----------------------|------------------|-----------|
//   | unset    | sentinel   | available      | -                    | true (fallback)  | yes       |
//   | unset    | sentinel   | none           | -                    | false            | no        |
//   | unset    | real value | any            | -                    | false            | no        |
//   | set      | real value | any            | no                   | true (mask)      | yes       |
//   | set      | real value | any            | yes                  | true (mask)      | no (hidden while editing) |
export const LoadstarSourceBit = {
    foreDraft:  1 << 0,  // 1
    aftDraft:   1 << 1,  // 2
    fluidGm:    1 << 2,  // 4
    solidGm:    1 << 3,  // 8
    rollAngle:  1 << 4,  // 16
    reefers:    1 << 5,  // 32
} as const;

export type LoadstarSourceFields = {
    foreDraft:  boolean;
    aftDraft:   boolean;
    fluidGm:    boolean;
    solidGm:    boolean;
    rollAngle:  boolean;
    reefers:    boolean;
};

export function encodeLoadstarSource(fields: Partial<LoadstarSourceFields>): number {
    let mask = 0;
    for (const [key, bit] of Object.entries(LoadstarSourceBit)) {
        if (fields[key as keyof LoadstarSourceFields]) mask |= bit;
    }
    return mask;
}

export function decodeLoadstarSource(mask: number | null | undefined): LoadstarSourceFields {
    return {
        foreDraft: !!((mask ?? 0) & LoadstarSourceBit.foreDraft),
        aftDraft:  !!((mask ?? 0) & LoadstarSourceBit.aftDraft),
        fluidGm:   !!((mask ?? 0) & LoadstarSourceBit.fluidGm),
        solidGm:   !!((mask ?? 0) & LoadstarSourceBit.solidGm),
        rollAngle: !!((mask ?? 0) & LoadstarSourceBit.rollAngle),
        reefers:   !!((mask ?? 0) & LoadstarSourceBit.reefers),
    };
}

export abstract class LoadstarModelBase extends BaseModel {
    abstract get exists(): boolean | null;
    abstract initialise(): Promise<void>;
    abstract checkExistsByImo(imo?: IMO): Promise<boolean>;
    abstract currentImoLoadstarExists(): Promise<boolean>;
    abstract getValueFromLoadstar(imo?: IMO, scheduleid?: string, voyageNo?: string): Promise<LoadstarValues | null>;
    abstract getCurrentAlert(): LoadstarNotificationType | null;
    abstract getLatestMatchingPlan(alert?: LoadstarNotificationType | null): Promise<VoyagePlan | null>;
    abstract createUpdatedPlan(plan: VoyagePlan, alert?: LoadstarNotificationType | null, overrides?: Partial<EditableLoadstarValues>): VoyagePlan;
    abstract openReviewChanges(alertId?: string): void;
    abstract dismissCurrentReview(alertId?: string, action?: 'Accepted' | 'Rejected'): void;
}

let LoadstarModel: LoadstarModelBase | null = null;

function setLoadstarModel(model: LoadstarModelBase) {
    LoadstarModel = model;
}

export { LoadstarModel, setLoadstarModel };
